import { authenticatedFetch } from './auth';
import type {
  ExamSet,
  ExamQuestion,
  ExamSession,
  ExamResult,
  AIGeneratedExam,
  CreateExamRequest,
  UpdateExamRequest,
  StartExamRequest,
  SaveAnswerRequest,
  UpdateFinalGradeRequest,
  SessionWithAnswers,
  AnswerWithQuestion,
  QuestionBankPreview,
  QuestionBankStats,
  QuestionBankValidation,
  DifficultyDistribution
} from '../types/exam';

const API_BASE = '/api';

// ==================== SEB DETECTION ====================

/**
 * Check if the current browser is Safe Exam Browser
 */
export function isSafeExamBrowser(): boolean {
  return navigator.userAgent.includes('SEB/');
}

/**
 * Get the sebs:// URL for opening an exam in SEB
 */
export function getSebsUrl(publicLink: string): string {
  return `sebs://${window.location.host}/exam/${publicLink}`;
}

/**
 * Get the sebs:// URL for opening an exam in SEB using invitation token
 */
export function getSebsUrlForInvitation(invitationToken: string): string {
  return `sebs://${window.location.host}/exam/invite/${invitationToken}`;
}

// ==================== EXAM MANAGEMENT ====================

export async function listExamSets(): Promise<ExamSet[]> {
  const response = await authenticatedFetch(`${API_BASE}/exams/list`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.exam_sets;
}

export async function getExamSet(id: string): Promise<{ exam_set: ExamSet; questions: ExamQuestion[] }> {
  const response = await authenticatedFetch(`${API_BASE}/exams/${id}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return { exam_set: data.exam_set, questions: data.questions };
}

export async function createExamSet(request: CreateExamRequest): Promise<ExamSet> {
  const response = await authenticatedFetch(`${API_BASE}/exams/create`, {
    method: 'POST',
    body: JSON.stringify(request)
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.exam_set;
}

export async function updateExamSet(id: string, request: UpdateExamRequest): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/exams/${id}`, {
    method: 'PUT',
    body: JSON.stringify(request)
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
}

export async function publishExamSet(id: string): Promise<string> {
  const response = await authenticatedFetch(`${API_BASE}/exams/${id}/publish`, {
    method: 'POST'
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.public_link;
}

export async function deleteExamSet(id: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/exams/${id}`, {
    method: 'DELETE'
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
}

// ==================== AI GENERATION ====================

export async function generateExamQuestions(studyPlanId: string): Promise<AIGeneratedExam> {
  const response = await authenticatedFetch(`${API_BASE}/exams/generate`, {
    method: 'POST',
    body: JSON.stringify({ study_plan_id: studyPlanId })
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.generated_exam;
}

// ==================== PUBLIC EXAM TAKING ====================

export async function getPublicExam(publicLink: string): Promise<ExamSet> {
  const response = await fetch(`/api/public-exam/${publicLink}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.exam_set;
}

export async function startExamSession(publicLink: string, request: StartExamRequest): Promise<{
  session: ExamSession;
  questions: ExamQuestion[];
  exam_info: {
    title: string;
    description?: string;
    time_limit_minutes?: number;
    expires_at?: string;
  };
}> {
  const response = await fetch(`/api/public-exam/${publicLink}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data;
}

export async function getExamByUniqueCandidateId(uniqueCandidateId: string): Promise<{
  session: ExamSession;
  questions: ExamQuestion[];
  exam_info: {
    title: string;
    description?: string;
    time_limit_minutes?: number;
    pass_threshold?: number;
    time_remaining?: number;
  };
}> {
  const response = await fetch(`/api/take-exam/${uniqueCandidateId}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data;
}

export interface ExamApiError extends Error {
  error_code?: string;
  exam_info?: {
    title?: string;
    started_at?: string;
    expired_at?: string;
    submitted_at?: string;
  };
  message_detail?: string;
}

export async function startExamWithCandidateCode(candidateCode: string): Promise<{
  session: ExamSession;
  questions: ExamQuestion[];
  exam_info: {
    title: string;
    description?: string;
    time_limit_minutes?: number;
    expires_at?: string;
  };
  candidate_info: {
    full_name: string;
    candidate_code: string;
  };
}> {
  const response = await fetch(`/api/public-exam/candidate-code/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ candidate_code: candidateCode })
  });
  const data = await response.json();
  if (!data.success) {
    const error = new Error(data.error) as ExamApiError;
    error.error_code = data.error_code;
    error.exam_info = data.exam_info;
    error.message_detail = data.message;
    // Include session info for ALREADY_SUBMITTED handling
    (error as any).session_id = data.session_id;
    (error as any).anonymous_id = data.anonymous_id;
    (error as any).unique_candidate_id = data.unique_candidate_id;
    (error as any).contact_submitted = data.contact_submitted;
    throw error;
  }
  return data;
}

// ==================== INVITATION TOKEN FLOW ====================

export interface InvitationExamInfo {
  candidate: {
    full_name: string;
    candidate_code: string;
  };
  exam: {
    id: string;
    title: string;
    description?: string;
    time_limit_minutes?: number;
    seb_required: boolean;
    public_link?: string;
  };
  course: {
    name: string;
    organization: string;
  };
  status: string;
}

/**
 * Get exam info by invitation token (no auth required)
 */
export async function getExamByInvitationToken(token: string): Promise<InvitationExamInfo> {
  const response = await fetch(`/api/public-exam/invite/${token}`);
  const data = await response.json();
  if (!data.success) {
    const error = new Error(data.error) as ExamApiError;
    error.error_code = data.error_code;
    throw error;
  }
  return data;
}

/**
 * Check invitation status - lightweight endpoint for polling while waiting for SEB
 */
export interface InvitationStatus {
  success: boolean;
  status: 'invited' | 'exam_started' | 'exam_completed' | 'graded' | 'claimed';
  full_name: string;
  exam_title: string;
  submitted_at?: string;
  receipt_sent: boolean;
}

export async function checkInvitationStatus(token: string): Promise<InvitationStatus> {
  const response = await fetch(`/api/public-exam/invite/${token}/status`);
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error);
  }
  return data;
}

/**
 * Start exam session using invitation token (no auth required)
 * The candidate's contact info is already stored, no need to enter code
 */
export async function startExamByInvitationToken(token: string): Promise<{
  session: ExamSession;
  questions: ExamQuestion[];
  exam_info: {
    title: string;
    description?: string;
    time_limit_minutes?: number;
    expires_at?: string;
  };
  candidate_info: {
    full_name: string;
    candidate_code: string;
    email?: string;
    mobile_number?: string;
  };
}> {
  const response = await fetch(`/api/public-exam/invite/${token}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  const data = await response.json();
  if (!data.success) {
    const error = new Error(data.error) as ExamApiError;
    error.error_code = data.error_code;
    error.exam_info = data.exam_info;
    error.message_detail = data.message;
    // Include session info for ALREADY_SUBMITTED handling
    (error as any).session_id = data.session_id;
    (error as any).anonymous_id = data.anonymous_id;
    (error as any).unique_candidate_id = data.unique_candidate_id;
    (error as any).contact_submitted = data.contact_submitted;
    throw error;
  }
  return data;
}

export async function saveAnswer(sessionId: string, anonymousId: string, request: SaveAnswerRequest): Promise<void> {
  const response = await fetch(`/api/public-exam/session/${sessionId}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...request, anonymous_id: anonymousId })
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
}

export async function submitExam(sessionId: string, anonymousId: string): Promise<void> {
  const response = await fetch(`/api/public-exam/session/${sessionId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ anonymous_id: anonymousId })
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
}

// ==================== GRADING & REVIEW ====================

export async function triggerAIGrading(sessionId: string): Promise<{ graded_count: number }> {
  const response = await authenticatedFetch(`${API_BASE}/exams/sessions/${sessionId}/ai-grade`, {
    method: 'POST'
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return { graded_count: data.graded_count };
}

export async function getSessionReview(sessionId: string): Promise<{
  session: SessionWithAnswers;
  answers: AnswerWithQuestion[];
}> {
  const response = await authenticatedFetch(`${API_BASE}/exams/sessions/${sessionId}/review`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return { session: data.session, answers: data.answers };
}

export async function updateFinalGrade(answerId: string, request: UpdateFinalGradeRequest): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/exams/answers/${answerId}/final-grade`, {
    method: 'PUT',
    body: JSON.stringify(request)
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
}

export async function finalizeGrading(sessionId: string): Promise<{
  total_points: number;
  max_points: number;
  percentage: number;
  grade: string;
  passed: boolean;
  valid_until?: string;
}> {
  const response = await authenticatedFetch(`${API_BASE}/exams/sessions/${sessionId}/finalize`, {
    method: 'POST'
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.result;
}

export async function getSessionEvents(sessionId: string): Promise<{
  id: string;
  exam_session_id: string;
  event_type: string;
  event_description: string;
  performed_by_user_id: string | null;
  performed_by_user_name: string | null;
  metadata: any;
  created_at: string;
}[]> {
  const response = await authenticatedFetch(`${API_BASE}/exams/sessions/${sessionId}/events`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.events;
}

// ==================== RESULTS & SESSIONS ====================

export async function getExamResults(examId: string): Promise<ExamResult[]> {
  const response = await authenticatedFetch(`${API_BASE}/exams/${examId}/results`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.results;
}

export async function getPublicResult(uniqueCandidateId: string): Promise<ExamResult> {
  const response = await fetch(`/api/public-exam/result/${uniqueCandidateId}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.result;
}

export async function resetExamSession(sessionId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/exams/sessions/${sessionId}/reset`, {
    method: 'POST'
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
}

export async function getExamSessions(examId: string): Promise<(ExamSession & {
  percentage?: number;
  grade?: string;
  passed?: boolean;
})[]> {
  const response = await authenticatedFetch(`${API_BASE}/exams/${examId}/sessions`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.sessions;
}

// ==================== QUESTION SWAP ====================

export interface SwapStatus {
  can_swap: boolean;
  swap_count: number;
  swaps_remaining: number;
  has_answers: boolean;
  warning: string | null;
}

export interface SwapResult {
  questions: ExamQuestion[];
  swap_count: number;
  swaps_remaining: number;
  answers_cleared: boolean;
  message: string;
}

/**
 * Get swap status for a session
 */
export async function getSwapStatus(sessionId: string, anonymousId: string): Promise<SwapStatus> {
  const response = await fetch(
    `/api/public-exam/session/${sessionId}/swap-status?anonymous_id=${encodeURIComponent(anonymousId)}`
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return {
    can_swap: data.can_swap,
    swap_count: data.swap_count,
    swaps_remaining: data.swaps_remaining,
    has_answers: data.has_answers,
    warning: data.warning
  };
}

/**
 * Swap questions for a session
 */
export async function swapQuestions(sessionId: string, anonymousId: string): Promise<SwapResult> {
  const response = await fetch(`/api/public-exam/session/${sessionId}/swap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ anonymous_id: anonymousId })
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return {
    questions: data.questions,
    swap_count: data.swap_count,
    swaps_remaining: data.swaps_remaining,
    answers_cleared: data.answers_cleared,
    message: data.message
  };
}

// ==================== QUESTION BANK ====================

/**
 * Preview question bank availability for a study plan
 */
export async function previewQuestionBank(
  studyPlanId: string,
  questionCount: number = 20,
  difficultyDistribution?: DifficultyDistribution
): Promise<QuestionBankPreview> {
  let url = `${API_BASE}/exam-questions/preview/${studyPlanId}?questionCount=${questionCount}`;
  if (difficultyDistribution) {
    url += `&easy=${difficultyDistribution.easy}&medium=${difficultyDistribution.medium}&hard=${difficultyDistribution.hard}`;
  }
  const response = await authenticatedFetch(url);
  const data = await response.json();
  return data;
}

/**
 * Validate if study plan has enough questions for question bank mode
 */
export async function validateQuestionBank(
  studyPlanId: string,
  minQuestions: number = 10
): Promise<QuestionBankValidation> {
  const response = await authenticatedFetch(`${API_BASE}/exam-questions/validate/${studyPlanId}`, {
    method: 'POST',
    body: JSON.stringify({ minQuestions })
  });
  const data = await response.json();
  return data;
}

/**
 * Get question bank statistics for a study plan
 */
export async function getQuestionBankStats(studyPlanId: string): Promise<QuestionBankStats> {
  const response = await authenticatedFetch(`${API_BASE}/questions/study-plan/${studyPlanId}/stats`);
  const data = await response.json();
  return data;
}

/**
 * Generate questions to the question bank for a study plan
 */
export async function generateQuestionsToBank(
  studyPlanId: string,
  options: {
    questionsPerLiterature?: number;
    questionTypes?: string[];
    difficultyDistribution?: DifficultyDistribution;
    minimumQuestions?: number;  // Minimum total questions to generate
  } = {}
): Promise<{
  totalQuestionsGenerated: number;
  existingQuestionsBefore?: number;
  duplicateWarning?: string;  // Warning if >50 existing questions
  generated?: number;
  questions: Array<{ id: string; question_text: string; difficulty: string; status: string }>;
}> {
  const response = await authenticatedFetch(`${API_BASE}/questions/generate/study-plan/${studyPlanId}`, {
    method: 'POST',
    body: JSON.stringify({
      questionsPerLiterature: options.questionsPerLiterature || 5,
      questionTypes: options.questionTypes || ['multiple_choice', 'true_false'],
      difficultyDistribution: options.difficultyDistribution || { easy: 0.2, medium: 0.6, hard: 0.2 },
      minimumQuestions: options.minimumQuestions || 20  // Default to 20 minimum questions
    })
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data;
}

/**
 * Get all questions in the question bank for a study plan
 */
export async function getQuestionBankQuestions(
  studyPlanId: string,
  options: { status?: string; limit?: number; offset?: number } = {}
): Promise<{
  questions: Array<{
    id: string;
    question_text: string;
    question_type: string;
    difficulty: string;
    status: string;
    times_used: number;
    module_title?: string;
    literature_title?: string;
  }>;
  total: number;
}> {
  let url = `${API_BASE}/questions/study-plan/${studyPlanId}?limit=${options.limit || 100}&offset=${options.offset || 0}`;
  if (options.status) url += `&status=${options.status}`;
  const response = await authenticatedFetch(url);
  const data = await response.json();
  return data;
}

/**
 * Review a question (approve/reject)
 */
export async function reviewQuestion(
  questionId: string,
  status: 'approved' | 'rejected' | 'pending_review' | 'draft',
  reviewNotes?: string
): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/questions/${questionId}/review`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reviewNotes })
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
}

/**
 * Bulk review multiple questions
 */
export async function bulkReviewQuestions(
  questionIds: string[],
  status: 'approved' | 'rejected',
  reviewNotes?: string
): Promise<{ updated: number }> {
  const response = await authenticatedFetch(`${API_BASE}/questions/bulk-review`, {
    method: 'POST',
    body: JSON.stringify({ questionIds, status, reviewNotes })
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return { updated: data.updated };
}

// ==================== LITERATURE ARCHIVE ====================

export interface LiteratureStatus {
  id: string;
  title: string;
  download_status: 'pending' | 'downloaded' | 'failed' | 'unavailable';
  download_error?: string;
  original_url: string;
}

export interface ArchiveResult {
  studyPlan: string;
  archived: number;
  failed: number;
  results: Array<{
    success: boolean;
    literatureId: string;
    error?: string;
    textLength?: number;
  }>;
}

/**
 * Get literature status for a study plan
 */
export async function getLiteratureStatus(studyPlanId: string): Promise<LiteratureStatus[]> {
  const response = await authenticatedFetch(`${API_BASE}/study-plans/${studyPlanId}/literature`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error);
  return data.literature.map((lit: any) => ({
    id: lit.id,
    title: lit.title,
    download_status: lit.download_status || 'pending',
    download_error: lit.download_error,
    original_url: lit.original_url
  }));
}

/**
 * Archive all pending literature for a study plan
 */
export async function archiveStudyPlanLiterature(studyPlanId: string): Promise<ArchiveResult> {
  const response = await authenticatedFetch(`${API_BASE}/study-plans/${studyPlanId}/literature/download-all`, {
    method: 'POST'
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data;
}
